import { useEffect } from "react";
import { pushTimerEngagement } from "../../utils/gtm";

const MILESTONES = [15, 30, 60, 180];

/**
 * Dispara `timer_engagement` em cada marco de tempo na página.
 *
 * A contagem só avança com a aba visível: `visibilitychange` pausa e retoma o
 * cronômetro, então uma aba aberta em segundo plano e esquecida não gera
 * engajamento fictício.
 *
 * `resetKey` reinicia a contagem; passe o `pathname` para que cada rota da SPA
 * seja medida do zero.
 */
export function useTimerEngagement(resetKey: string): void {
  useEffect(() => {
    let pending = MILESTONES.slice();
    let timeout: number | undefined;
    let elapsed = 0; // segundos já acumulados com a aba visível
    let resumedAt = 0; // instante em que o trecho visível atual começou

    const schedule = (): void => {
      // O guard de `timeout` impede dois cronômetros em paralelo caso o
      // navegador emita `visibilitychange` visível duas vezes seguidas.
      if (timeout !== undefined || !pending.length || document.hidden) return;
      const next = pending[0];
      resumedAt = Date.now();
      timeout = window.setTimeout(() => {
        timeout = undefined;
        elapsed = next;
        pending = pending.slice(1);
        pushTimerEngagement(next);
        schedule();
      }, (next - elapsed) * 1000);
    };

    const pause = (): void => {
      if (timeout === undefined) return;
      window.clearTimeout(timeout);
      timeout = undefined;
      elapsed += (Date.now() - resumedAt) / 1000;
    };

    const handleVisibilityChange = (): void => {
      if (document.hidden) pause();
      else schedule();
    };

    schedule();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.clearTimeout(timeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [resetKey]);
}
